import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext, useLocation } from 'react-router-dom';
import { NJButton, NJCheckbox, NJText } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { UMFormStyles, UMIcon, UMR, UMSelect, UMSegmentedControl } from '../../urbanmap/_components';
import { BuildingPanel } from './BuildingPanel';
import { BuildingList, DataMap } from './DataView';
import { Comparison } from './Comparison';
import { ConfirmDialog } from './ConfirmDialog';
import { DecisionPanel } from './DecisionPanel';
import { DirtyContext } from './DirtyContext';
import { HypothesesPanel } from './HypothesesPanel';
import { useStudio } from './hooks';
import { Navigation } from './Navigation';
import { NetworkMap } from './NetworkMap';
import { PlanPanel } from './PlanPanel';
import { ProjectSettings } from './ProjectSettings';
import { RecalculateDialog } from './RecalculateDialog';
import { SyncPanel } from './SyncPanel';
import { Wireframes } from './Wireframes';
import type { Destination, Energy, Hypotheses, MapTool } from './types';
import './styles.css';

export const meta = {
  title: 'Plan unifié',
  description: 'Projet ouvert : données, corrections, synchronisation, scénarios et plans. Revue basse fidélité et décisions ouvertes explicites.',
  tags: ['editor', 'planning', 'network'],
  author: 'DT5993', status: 'draft', createdAt: '2026-10-19',
  updatedAt: '2026-10-20',
};

export default function PlanUnifie() {
  const navigation = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const studio = useStudio();
  const { state, mutation } = studio;
  const [screen, setScreen] = useState<Destination>('data');
  const [review, setReview] = useState(new URLSearchParams(window.location.search).get('view') !== 'demo');
  const [decisions, setDecisions] = useState(false);
  const [decisionId, setDecisionId] = useState<string | null>(null);
  const [buildingId, setBuildingId] = useState('c1');
  const [scenarioId, setScenarioId] = useState(state.scenarios[0]?.id ?? '');
  const [planId, setPlanId] = useState<string | null>('p1');
  const [energy, setEnergy] = useState<Energy>('heat');
  const [road, setRoad] = useState('r9');
  const [tool, setTool] = useState<MapTool>('inspect');
  const [dirty, setDirty] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, boolean>>({});
  const [editingKey, setEditingKey] = useState(0);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [recalculate, setRecalculate] = useState(false);
  const [fail, setFail] = useState(false);
  const [notice, setNotice] = useState('');
  const scenario = state.scenarios.find(s => s.id === scenarioId) ?? state.scenarios[0];
  const plan = state.plans.find(p => p.id === planId);
  const building = state.buildings.find(b => b.id === buildingId);
  const unsaved = dirty || Object.values(drafts).some(Boolean);
  const unsavedRef = useRef(unsaved);
  const historyIndex = useRef<number | undefined>(window.history.state?.idx);
  const allowPop = useRef(false);
  useEffect(() => { unsavedRef.current = unsaved; }, [unsaved]);
  useEffect(() => { historyIndex.current = window.history.state?.idx; }, [location]);
  useEffect(() => {
    const navigator = navigation.navigator as unknown as {
      push: (...args: unknown[]) => void;
      replace: (...args: unknown[]) => void;
    };
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;
    const guard = (original: (...args: unknown[]) => void) => (...args: unknown[]) => {
      if (unsavedRef.current) {
        setPendingNavigation(() => () => original.apply(navigator, args));
      } else original.apply(navigator, args);
    };
    navigator.push = guard(originalPush);
    navigator.replace = guard(originalReplace);
    return () => {
      if (navigator.push !== originalPush) navigator.push = originalPush;
      if (navigator.replace !== originalReplace) navigator.replace = originalReplace;
    };
  }, [navigation.navigator]);
  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      const current = historyIndex.current;
      const target = window.history.state?.idx;
      if (allowPop.current) {
        allowPop.current = false;
        historyIndex.current = target;
        return;
      }
      if (!unsavedRef.current || typeof current !== 'number' || typeof target !== 'number' || current === target) {
        historyIndex.current = target;
        return;
      }
      event.stopImmediatePropagation();
      const delta = current - target;
      setPendingNavigation(() => () => {
        allowPop.current = true;
        window.history.go(-delta);
      });
      window.history.go(delta);
    };
    window.addEventListener('popstate', onPop, true);
    return () => window.removeEventListener('popstate', onPop, true);
  }, []);
  const reportDraft = useCallback((id: string, value: boolean) => setDrafts(previous => {
    if (previous[id] === value || (!value && !(id in previous))) return previous;
    const next = { ...previous };
    if (value) next[id] = true; else delete next[id];
    return next;
  }), []);
  useEffect(() => {
    if (!document.querySelector(`link[href="${UMR.fontHref}"]`)) {
      const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = UMR.fontHref; document.head.appendChild(link);
    }
  }, []);
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => { if (unsaved) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [unsaved]);
  useEffect(() => {
    if (decisions || review || !decisionId) return;
    const timer = window.setTimeout(() => {
      const note = [...document.querySelectorAll<HTMLElement>('.pu-root [data-decision-id]')]
        .find(element => element.dataset.decisionId === decisionId && element.getClientRects().length > 0);
      note?.scrollIntoView({ block: 'center' });
      note?.querySelector('button')?.focus({ preventScroll: true });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [screen, decisions, review, decisionId]);
  const request = (action: () => void) => {
    if (mutation.isPending) { setNotice('Calcul en cours : attendez la fin avant de modifier le projet.'); return; }
    if (unsaved) setPending(() => action);
    else { setTool('inspect'); action(); }
  };
  const demo = () => {
    setReview(false);
    const url = new URL(window.location.href); url.searchParams.set('view', 'demo'); window.history.replaceState(null, '', url);
  };
  const visit = (destination: Destination, choice?: string) => request(() => {
    demo(); setDecisions(false); setScreen(destination);
    setDecisionId(choice ?? null);
    if (destination === 'plan' && !plan) setPlanId(state.plans.find(p => p.scenarioId === scenario?.id)?.id ?? null);
    if (destination === 'scenario') setPlanId(null);
  });
  const openDecision = (id: string) => request(() => { setDecisionId(id); setDecisions(true); });
  const select = (s: string, p?: string) => request(() => {
    setScenarioId(s); setPlanId(p ?? null); setScreen(p ? 'plan' : 'scenario'); mutation.reset();
  });
  const run = (h: Hypotheses) => {
    if (!plan || state.buildings.some(b => b.conflict && b.id in plan.subscribers)) {
      setNotice('Calcul non lancé : conflit de données à arbitrer.'); return;
    }
    mutation.mutate({ plan: structuredClone(plan), hypotheses: structuredClone(h), networkKind: state.project.networkKind, fail });
  };
  const isStudy = screen === 'scenario' || screen === 'plan' || screen === 'compare';
  const settingsOrSync = screen === 'settings' || screen === 'sync';
  return <PrototypeStudioLayout title={meta.title}>
    <DirtyContext.Provider value={reportDraft}>
      <div className="pu-root sp-root" style={{ fontFamily: UMR.font }}>
        <UMFormStyles />
        <header className="sp-header">
          <div className="sp-logo"><UMIcon name="hub" size={26} /><NJText as="div"><strong>PLAN</strong></NJText></div>
          <div className="sp-header-title"><NJText as="div"><strong>{state.project.name}</strong></NJText>
            <NJText as="div" scale="sm">Gentilly · projet ouvert · données de démonstration</NJText></div>
          <NJButton label="Revue basse fidélité" scale="sm" emphasis="minimal" onClick={() => request(() => {
            setDecisions(false); setReview(true);
            const url = new URL(window.location.href); url.searchParams.set('view', 'wireframes'); window.history.replaceState(null, '', url);
          })} />
          <NJButton label="Paramètres" icon="settings" scale="sm" emphasis="subtle" onClick={() => visit('settings')} />
          <NJButton label={state.synced ? 'Écarts UM · 2 en attente' : 'Écarts UM · 3'} icon="sync" scale="sm" emphasis="subtle" onClick={() => visit('sync')} />
          <NJButton label="Décisions ouvertes · 10" icon="help_outline" scale="sm" emphasis="subtle"
            onClick={() => request(() => { setDecisionId(null); setDecisions(true); })} />
        </header>
        <div className="pu-spacebar">
          <UMSegmentedControl options={['Données du projet', 'Étude']} value={isStudy ? 'Étude' : 'Données du projet'}
            onChange={v => visit(v === 'Étude' ? (plan ? 'plan' : 'scenario') : 'data')} />
          <NJText scale="sm">Prototype · règles confirmées / hypothèses signalées / données fictives</NJText>
          {isStudy && <NJButton label="Comparer les plans" icon="compare_arrows" scale="sm" emphasis="subtle" onClick={() => visit('compare')} />}
        </div>
        {studio.storageError && <div className="sp-error" role="alert"><NJText>{studio.storageError}</NJText>
          <NJButton label="Réinitialiser cette démonstration" onClick={() => request(studio.reset)} /></div>}
        {notice && <div className="sp-warning" role="status"><NJText>{notice}</NJText></div>}
        <div hidden={!decisions}><DecisionPanel selected={decisionId} visit={visit} close={() => setDecisions(false)} /></div>
        {review && !decisions && <Wireframes visit={visit} close={demo} />}
        <fieldset className="pu-workbench" disabled={mutation.isPending} hidden={review || decisions}>
          <legend className="sp-sr-only">Projet Plan unifié</legend>
          <div className="sp-workspace" hidden={isStudy}>
            <BuildingList buildings={state.buildings} project={state.project} energy={energy} selected={buildingId}
              select={id => request(() => { setBuildingId(id); setScreen('data'); })} onDecision={openDecision} />
            <main className="sp-main">
              <div className="sp-breadcrumb"><NJText>Données du projet · révision {state.project.revision}</NJText>
                <UMSelect ariaLabel="Énergie affichée sur la carte" value={energy}
                  options={[{ value: 'heat', label: 'Chaleur' }, { value: 'cold', label: 'Froid' }]}
                  onChange={v => { if (v === 'heat' || v === 'cold') setEnergy(v); }} /></div>
              <div className="sp-content">
                <DataMap buildings={state.buildings} project={state.project} energy={energy} selected={buildingId}
                  select={id => request(() => { setBuildingId(id); setScreen('data'); })} />
                <div className="pu-inspector" key={`${screen}-${buildingId}-${editingKey}`}>
                  {screen === 'settings' ? <ProjectSettings project={state.project} buildings={state.buildings}
                    save={studio.saveProject} dirty={setDirty} onDecision={openDecision} /> :
                    screen === 'sync' ? <SyncPanel studio={studio} onDecision={openDecision} /> :
                    building && <BuildingPanel building={building} project={state.project} save={studio.correct}
                      dirty={setDirty} onDecision={openDecision} />}
                </div>
              </div>
              {settingsOrSync && <NJButton label="Revenir aux données du projet" emphasis="minimal" onClick={() => visit('data')} />}
            </main>
          </div>
          <div hidden={screen !== 'compare'}><Comparison plans={state.plans} scenarios={state.scenarios} onDecision={openDecision}
            open={id => { const p = state.plans.find(item => item.id === id); if (p) select(p.scenarioId, p.id); }} />
          </div>
          <div className="sp-workspace" hidden={!isStudy || screen === 'compare'}>
            <Navigation scenarios={state.scenarios} plans={state.plans} scenarioId={scenario?.id ?? ''} planId={screen === 'scenario' ? null : planId}
              select={select} addScenario={() => request(() => { const id = studio.addScenario(); setScenarioId(id); setPlanId(null); setScreen('scenario'); })}
              addPlan={id => request(() => { setScenarioId(id); setPlanId(studio.addPlan(id)); setScreen('plan'); })} />
            <main className="sp-main">
              <div className="sp-breadcrumb"><NJText as="div">Étude / {scenario?.name} / {screen === 'plan' ? plan?.name : 'Hypothèses'}</NJText></div>
              {scenario && <div className="sp-content">
                <NetworkMap hypotheses={scenario.hypotheses} plan={screen === 'plan' ? plan : undefined} selectedRoad={road}
                  onSelect={id => request(() => setRoad(id))} tool={tool} networkKind={state.project.networkKind}
                  onFinish={() => setTool('inspect')}
                  onDeparture={d => studio.updateHypotheses(scenario.id, { ...scenario.hypotheses, departures: [...scenario.hypotheses.departures, d] })}
                  onManual={pipe => { if (plan) studio.updatePlan(plan.id, { pipes: [...plan.pipes, pipe] }); }} />
                <div className="pu-inspector" key={`${scenario.id}-${planId}-${editingKey}-${screen}`}>
                  {screen === 'plan' && plan ? <PlanPanel plan={plan} hypotheses={scenario.hypotheses} buildings={state.buildings} project={state.project}
                    onDecision={openDecision} focusDecision={decisions ? null : decisionId} busy={mutation.isPending} error={mutation.error?.message}
                    update={patch => studio.updatePlan(plan.id, patch)} draw={() => request(() => setTool('manual'))}
                    calculate={() => request(() => { mutation.reset(); if (plan.snapshot || plan.pipes.some(p => p.manual)) setRecalculate(true); else run(scenario.hypotheses); })} /> :
                    <HypothesesPanel scenario={scenario} selectedRoad={road} selectRoad={id => request(() => setRoad(id))}
                      networkKind={state.project.networkKind} setTool={t => request(() => setTool(t))}
                      onDecision={openDecision} save={h => studio.updateHypotheses(scenario.id, h)}
                      rename={name => studio.setState(s => ({ ...s, scenarios: s.scenarios.map(c => c.id === scenario.id ? { ...c, name } : c) }))} />}
                </div>
              </div>}
            </main>
          </div>
        </fieldset>
        <footer className="pu-statusbar">
          <NJText scale="sm">Sauvegarde locale indépendante · aucune valeur d’ingénierie</NJText>
          <NJCheckbox label="Simuler un échec de calcul" isChecked={fail} onChange={(_, v) => setFail(!!v)} />
          {mutation.isPending && <NJText role="status">Calcul du tracé en cours…</NJText>}
          {mutation.isSuccess && <NJText role="status">Dernier calcul réussi. Consultez l’état du plan et complétez le phasage.</NJText>}
        </footer>
        {(pending || pendingNavigation) && <ConfirmDialog title="Modifications non enregistrées" confirmLabel="Abandonner et continuer"
          close={() => {
            setPending(null);
            setPendingNavigation(null);
          }}
          confirm={() => {
            const next = pending ?? pendingNavigation;
            setPending(null);
            setPendingNavigation(null);
            setDirty(false);
            setDrafts({});
            setEditingKey(k => k + 1);
            setTool('inspect');
            next?.();
          }}>
          <NJText>Quitter ce contexte abandonne le brouillon, pas les valeurs enregistrées.
            Annulez pour revenir au formulaire et enregistrer vos modifications.</NJText>
        </ConfirmDialog>}
        {recalculate && plan && scenario && <RecalculateDialog plan={plan} current={scenario.hypotheses}
          onClose={() => setRecalculate(false)} onConfirm={h => { setRecalculate(false); run(h); }} />}
      </div>
    </DirtyContext.Provider>
  </PrototypeStudioLayout>;
}
